import * as THREE from 'three';
import { GameMode } from './GameMode.js';
import { BOMB_RULES as R } from './rules.js';
import { buildSiteMarkers } from '../gameplay/SiteMarkers.js';
import { MatchStats } from './MatchStats.js';
import { computeAwards } from './awards.js';

/**
 * How long the award screens hold before the next match starts.
 *
 * Matched to the server's MATCH_INTERMISSION, and for the same reason: it is
 * six pages, and the ten seconds the old single-line result got was under two
 * seconds a page.
 */
const MATCH_INTERMISSION = 22;

const _v = new THREE.Vector3();

/**
 * Counter-Strike bomb defusal.
 *
 * Phases: freeze -> live -> (planted) -> over -> freeze...
 *
 * The rule most often got wrong is encoded explicitly in `_checkWin`: **once
 * the bomb is planted, eliminating every attacker does NOT end the round.**
 * The defenders still have to defuse it. Getting that backwards turns the mode
 * into team deathmatch with extra steps.
 */
export class BombDefusal extends GameMode {
  static id = 'bomb';

  constructor(game, opts) {
    super(game, opts);
    this.phase = 'freeze';
    this.clock = R.freezeTime;
    this.round = 1;
    this.score = { red: 0, blue: 0 };
    this.winner = null;
    this.reason = null;
    this.playerTeam = game.playerTeam;
    /**
     * Set once a side has taken the match. Until this existed the offline mode
     * quietly zeroed the score and rolled straight into the next match, so
     * winning one was announced by a three-second banner and nothing else.
     */
    this.matchWinner = null;
    this.awards = null;
    /** Per-match stats for the award screens. See MatchStats for the hooks. */
    this.stats = new MatchStats();
  }

  start() {
    // Paint A and B on the ground. Being told to plant at A with nothing in the
    // world showing where A is makes the objective unfindable.
    buildSiteMarkers(this.game.level, this.game.physics);
    this._startRound();
  }

  get inputLocked() {
    return this.phase === 'freeze' || this.phase === 'over';
  }

  /* ------------------------------------------------------------ rounds -- */

  _startRound() {
    const g = this.game;

    // Clear the field. `despawn()` rather than setting `_removed` by hand:
    // the flag is only acted on inside Enemy.update(), and emptying the array
    // on the next line means update() never runs again — which left every bot
    // from the previous round standing frozen in the map, and left the squad
    // blackboard holding dead references.
    for (const e of g.enemies) e.despawn();
    g.enemies.length = 0;
    g.rockets.length = 0;
    for (const t of g.throwables) {
      g.physics.removeBody(t.body);
      t.mesh.visible = false;
    }
    g.throwables.length = 0;
    // Craters, scorch decals, debris and smoke are pooled and outlive a round
    // on their own timers, so the new round would open on the previous round's
    // detonation.
    g.effects.clear();

    const spawns = g.level.teamSpawns ?? {};
    const pick = (arr, i) => (arr?.length ? arr[i % arr.length] : g.level.spawnPoints[0]);

    for (let i = 0; i < R.botsPerTeam; i++) {
      // One fewer bot on the player's side — the player fills that slot.
      //
      // `slot` is what makes a bot the same soldier from round to round for the
      // award stats: the Enemy object and its id are both thrown away and
      // rebuilt here every round, so neither can carry a match-long tally.
      if (this.playerTeam !== 'red' || i < R.botsPerTeam - 1) {
        const e = g.spawnEnemy('red', pick(spawns.red, i).clone());
        if (e) e.slot = `red${i}`;
      }
      if (this.playerTeam !== 'blue' || i < R.botsPerTeam - 1) {
        const e = g.spawnEnemy('blue', pick(spawns.blue, i).clone());
        if (e) e.slot = `blue${i}`;
      }
    }

    // Player back in the fight on their own side, facing the map middle.
    const mine = pick(spawns[this.playerTeam], R.botsPerTeam);
    const yaw = Math.atan2(-(0 - mine.x), -(0 - mine.z));
    g.player.respawn(mine, yaw);
    g.weapon.ammo = g.weapon.spec.mag;
    g.weapon.reserve = g.weapon.spec.reserve;
    g.progress = null;

    // The bomb goes to the player if they are red, else to a red bot.
    const carrier = this.playerTeam === 'red'
      ? g.player
      : g.enemies.find((e) => e.team === 'red');
    g.bomb.reset(carrier);

    // Red commits to one site per round; blue splits between both. Choosing
    // once (rather than per tick) is what makes an attack read as coordinated.
    const sites = g.level.bombSites;
    this.targetSite = sites[(Math.random() * sites.length) | 0];

    this._nav()?.clearBlocks();
    this.phase = 'freeze';
    this.clock = R.freezeTime;
    this.winner = null;
    this.reason = null;
    this._assignAt = 0;
    g.hud.showObjective?.(`ROUND ${this.round}`,
      this.playerTeam === 'red' ? 'PLANT THE BOMB AT A OR B' : 'PREVENT THE PLANT', 2.5);
  }

  _endRound(winner, reason) {
    if (this.phase === 'over') return;
    this.phase = 'over';
    this.clock = R.postRound;
    this.winner = winner;
    this.reason = reason;
    this.score[winner]++;

    // Best of seven: a clinch ends it early, and the round cap guarantees it
    // ends at all. Seven is odd, so a bomb match cannot be drawn.
    const red = this.score.red, blue = this.score.blue;
    if (Math.max(red, blue) >= R.roundsToWin || this.round >= R.roundsPerMatch) {
      this.matchWinner = red === blue ? 'draw' : red > blue ? 'red' : 'blue';
      this.awards = computeAwards(this.stats.toRows());
      this.game.matchAwards.show({
        winner: this.matchWinner,
        red, blue,
        mine: this.playerTeam,
        ffa: false,
        awards: this.awards,
        rows: this.stats.toRows(),
      });
    }

    const won = winner === this.playerTeam;
    const text = {
      defused: 'BOMB DEFUSED',
      exploded: 'BOMB DETONATED',
      elimination: 'TEAM ELIMINATED',
      time: 'TIME EXPIRED',
    }[reason] ?? reason;
    this.game.hud.showObjective?.(won ? 'ROUND WON' : 'ROUND LOST', text, R.postRound);
  }

  /**
   * Wipe the slate and play another one.
   *
   * Stats reset with the score: a scoreboard that carries the last match's
   * kills into this one is a scoreboard of nothing.
   */
  _newMatch() {
    this.matchWinner = null;
    this.awards = null;
    this.round = 1;
    this.score.red = 0;
    this.score.blue = 0;
    this.stats.reset();
    this.game.matchAwards.hide();
    this._startRound();
  }

  /* ------------------------------------------------------------ update -- */

  update(dt) {
    const g = this.game;
    this.clock -= dt;
    // Ground covered, for the LONG RUNNER award. Sampled every frame because
    // movement has no single choke point offline.
    this.stats.sample([g.player, ...g.enemies]);

    if (this.phase === 'freeze') {
      if (this.clock <= 0) {
        this.phase = 'live';
        this.clock = R.roundTime;
      }
      return;
    }

    if (this.phase === 'over') {
      // A decided match holds on the award screens, then starts a new one.
      if (this.matchWinner) {
        if (this.clock <= -MATCH_INTERMISSION) this._newMatch();
        return;
      }
      if (this.clock <= 0) { this.round++; this._startRound(); }
      return;
    }

    // Bots: reassign on a slow timer, but immediately when the bomb state
    // changes — a plant should flip everyone's job in the same tick.
    this._assignAt -= dt;
    // Deliberately NOT serviced the instant a bot asks: re-pathing on demand
    // resets its route progress, and measured across 20 rounds that dropped
    // plants from 40% to 15%. A wedged bot waits out the tick.
    if (this._assignAt <= 0 || this._lastBombState !== g.bomb.state) {
      this._assignAt = 1.0;
      this._lastBombState = g.bomb.state;
      this._assignObjectives();
    }
    this._updateBotInteractions(dt);

    // live / planted
    if (g.bomb.state === 'planted' && this.phase !== 'planted') {
      this.phase = 'planted';
      g.hud.showObjective?.('BOMB PLANTED',
        this.playerTeam === 'blue' ? 'DEFUSE IT' : 'DEFEND THE BOMB', 2.5);
    }
    this._checkWin();
  }

  /**
   * Win conditions, in priority order. Order matters: defused and exploded are
   * terminal states that must win regardless of who is still alive.
   */
  _checkWin() {
    const g = this.game;
    const bomb = g.bomb;

    if (bomb.state === 'defused') return this._endRound('blue', 'defused');
    if (bomb.state === 'exploded') return this._endRound('red', 'exploded');

    const alive = (team) => {
      let n = g.enemies.filter((e) => e.alive && e.team === team).length;
      if (g.player.team === team && g.player.alive) n++;
      return n;
    };
    const red = alive('red');
    const blue = alive('blue');

    if (blue === 0) return this._endRound('red', 'elimination');

    // THE rule: with the bomb down, wiping out the attackers wins nothing —
    // the defenders must still defuse it before the fuse runs out.
    if (red === 0 && bomb.state !== 'planted') {
      return this._endRound('blue', 'elimination');
    }

    // The round clock only matters while the bomb is unplanted. Once it is
    // down, only the fuse counts.
    if (this.phase !== 'planted' && this.clock <= 0) {
      return this._endRound('blue', 'time');
    }
    return null;
  }

  /* --------------------------------------------------------- bot brain -- */

  /**
   * Give every bot a job appropriate to the phase.
   *
   * Cheap by design: reassign only when something has actually changed, and
   * pick from the level's authored routes rather than pathfinding. The bots
   * still fight using the existing state machine — this only tells them *where*
   * to be when nobody is shooting at them.
   */
  _assignObjectives() {
    const g = this.game;
    const bomb = g.bomb;
    // Hand-authored routes win if a level declares them; otherwise generate.
    const authored = g.level.constructor.bombRoutes ?? g.level.bombRoutes ?? null;

    /**
     * Assign a job, pathing only when the job actually changed. Routing all
     * eight bots every second would be pure waste — and `setObjective` would
     * throw the fresh route away anyway to protect their progress.
     */
    const assign = (e, kind, dest, radius) => {
      const pos = dest.center ?? dest;
      // A bot that wedged itself asks for a new path; that request has to beat
      // the keep-your-progress guard or it would be ignored forever.
      if (!e.needsRepath && e.objective && e.objective.kind === kind
        && e.objective.pos.distanceToSquared(pos) < 4) {
        e.setObjective(kind, pos, null, radius);
        return;
      }
      const a = authored?.[e.team]?.[dest.label ?? ''];
      const route = a
        ? a.map((q) => new THREE.Vector3(q[0], q[1], q[2]))
        : this._nav()?.route(e.root.position, pos) ?? null;
      /*
       * Repeated wedges at the same spot mean the grid is lying about a gap,
       * not that the bot was unlucky. Tell the grid, so the next route goes
       * round instead of back into the same corner — otherwise the bot burns
       * the whole round on repath/reverse (measured: 590 repaths, no progress).
       */
      if (e.needsRepath) {
        const here = e.root.position;
        if (e._wedgeAt && e._wedgeAt.distanceToSquared(here) < 25) {
          e._wedgeHits = (e._wedgeHits ?? 0) + 1;
          if (e._wedgeHits >= 1) {
            // Blame the ground just ahead, which is where it keeps jamming.
            const ahead = here.clone().lerp(pos, 0).add(
              _v.subVectors(pos, here).setY(0).normalize().multiplyScalar(1.8),
            );
            ahead.y = here.y;
            this._nav()?.block(ahead, 2.0);
            e._wedgeHits = 0;
            e._wedgeAt = null;
          }
        } else {
          e._wedgeAt = here.clone();
          e._wedgeHits = 0;
        }
      }

      e.needsRepath = false;
      e.objective = null;                 // force the fresh route to be taken
      e.setObjective(kind, pos, route, radius);
    };

    // Closest living attacker to a dropped bomb goes back for it; the rest
    // keep pushing so the retrieval is covered.
    let retriever = null;
    if (bomb.state === 'dropped') {
      let best = Infinity;
      for (const e of g.enemies) {
        if (!e.alive || e.team !== 'red') continue;
        const d = e.root.position.distanceToSquared(bomb.position);
        if (d < best) { best = d; retriever = e; }
      }
    }

    for (const e of g.enemies) {
      if (!e.alive) continue;

      if (e.team === 'red') {
        if (bomb.state === 'dropped' && e === retriever) {
          // Somebody has to go and get it. Without this the round is simply
          // unwinnable for red the moment the carrier dies — which is what
          // held the bot-only sim at zero plants.
          assign(e, 'retrieve', bomb.position, 1.2);
        } else if (bomb.state === 'planted') {
          // Post-plant: hold near the bomb rather than keep pushing. This is
          // the behaviour that makes the mode read as CS.
          assign(e, 'hold', bomb.position, 9);
        } else if (bomb.carrier === e) {
          const site = this._siteFor(e);
          assign(e, 'plant', site, this._siteRadius(site));
        } else {
          const site = this._siteFor(e);
          assign(e, 'escort', site, this._siteRadius(site) + 3);
        }
      } else {
        if (bomb.state === 'planted') {
          assign(e, 'defuse', bomb.position, 1.5);
        } else {
          const site = this._siteFor(e);
          assign(e, 'defend', site, this._siteRadius(site) + 4);
        }
      }
    }
  }

  /**
   * The walkable grid for this level, built on first use and cached on the
   * level so it survives round restarts (it costs a few thousand raycasts).
   */
  _nav() { return this.game.nav(); }

  /**
   * How close a bot must get to count as "at the site".
   *
   * Derived from the site volume, not a constant: Dust II's A site is 9.75 m
   * across, so a fixed 2.2 m radius asks a bot to hit its exact centre, and
   * measurement showed the carrier parking ~4 m short and never planting. CS
   * lets you plant anywhere inside the site, and so does this.
   */
  _siteRadius(site) {
    const s = site.box.getSize(new THREE.Vector3());
    return Math.min(6, Math.max(2.5, Math.min(s.x, s.z) * 0.45));
  }

  /** Which site is this bot working? Chosen once per round, split across both. */
  _siteFor(e) {
    const sites = this.game.level.bombSites;
    if (this.targetSite && e.team === 'red') return this.targetSite;
    if (e._siteChoice == null) e._siteChoice = e.id % sites.length;
    return sites[e._siteChoice];
  }

  /**
   * Drive bot plant/defuse through the SAME timers the player uses, so there is
   * one implementation of the rules rather than two that can drift apart.
   */
  _updateBotInteractions(dt) {
    const g = this.game;
    const bomb = g.bomb;

    for (const e of g.enemies) {
      if (!e.alive || !e.interact) { if (e.interact) e._act = 0; continue; }

      // Waiting for the site to be clear sounds right and is not: a contested
      // site never clears, so the bot stands on the bomb doing nothing until
      // the clock runs out. Once committed, it plants under fire.
      if (e.state === 'engage' && !e.committed) { e._act = 0; continue; }

      if (e.interact === 'plant') {
        if (bomb.carrier !== e || bomb.state !== 'carried') { e._act = 0; continue; }
        const site = this._siteFor(e);
        if (!site.box.containsPoint(e.root.position)
          && site.center.distanceTo(e.root.position) > 3.5) { e._act = 0; continue; }
        e._act = (e._act ?? 0) + dt;
        if (e._act >= R.plantTime) {
          bomb.plant(e.root.position.clone(), site.label);
          this.stats.onPlant(e);
          e._act = 0;
          e.clearObjective();
        }
      } else if (e.interact === 'defuse') {
        if (bomb.state !== 'planted'
          || bomb.position.distanceTo(e.root.position) > R.useRange + 0.8) { e._act = 0; continue; }
        e._act = (e._act ?? 0) + dt;
        if (e._act >= R.defuseTime) {
          bomb.defuse();
          this.stats.onDefuse(e);
          e._act = 0;
        }
      } else {
        e._act = 0;
      }
    }
  }

  /* ------------------------------------------------------------- hooks -- */

  onPlayerDeath() {
    // Dead until the next round — that is the whole tension of the mode.
    this.game.respawnTimer = Infinity;
    return true;
  }

  decorateHud(s) {
    const g = this.game;
    const bomb = g.bomb;
    // Carrying the bomb is the single most important thing about your round and
    // nothing on screen said so.
    s.carryingBomb = bomb?.carrier === g.player;
    s.bombState = bomb?.state ?? null;
    // Where the loose bomb is, for the on-screen marker. Only your own side
    // needs to find a dropped one; a planted one matters to everybody.
    if (bomb && (bomb.state === 'dropped'
      || (bomb.state === 'planted' && this.phase === 'planted'))) {
      s.bombWorldPos = bomb.position;
      s.bombMarker = bomb.state === 'dropped' ? 'DROPPED' : `BOMB ${bomb.site ?? ''}`.trim();
    } else {
      s.bombWorldPos = null;
      s.bombMarker = null;
    }
    // Which site the bomb is actually at, so the minimap can highlight it.
    s.bombSiteLive = bomb?.state === 'planted' ? bomb.site : null;
    s.roundPhase = this.phase;
    s.roundClock = Math.max(0, this.clock);
    s.teamScore = { red: this.score.red, blue: this.score.blue, mine: this.playerTeam };
    s.round = this.round;
    s.playerTeam = this.playerTeam;
    s.roundWinner = this.winner;
    s.roundReason = this.reason;
    s.matchWinner = this.matchWinner;
    s.roundsToWin = R.roundsToWin;
    s.roundsPerMatch = R.roundsPerMatch;
  }
}
